import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { api, errorMessage, isRecord } from './api';
import { PERMISSIONS } from './constants';
import { useRealtime } from './useRealtime';
import type {
  LoginResponse,
  OkResponse,
  RealtimeEvent,
  Role,
  SessionResponse,
  ToastKind,
  UserWithRole,
} from './types';
import { ErrorBox, HintBox, Loading } from './components/Feedback';
import { PasswordDialog } from './components/PasswordDialog';
import { UserMenu } from './components/UserMenu';
import { BoardPage } from './pages/BoardPage';
import { CloudPage } from './pages/CloudPage';
import { HistoryPage } from './pages/HistoryPage';
import { LoginPage } from './pages/LoginPage';
import { TeamPage } from './pages/TeamPage';

type TabId = 'board' | 'history' | 'team' | 'cloud';

interface ReadySession {
  user: UserWithRole;
  permissions: string[];
  roles: Role[];
}

type SessionState =
  | { phase: 'loading' }
  | { phase: 'anonymous' }
  | { phase: 'ready'; session: ReadySession };

interface Toast {
  id: number;
  kind: ToastKind;
  text: string;
}

let toastCounter = 1;

function useToasts(): {
  toasts: Toast[];
  push: (kind: ToastKind, text: string) => void;
  dismiss: (id: number) => void;
} {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const dismiss = useCallback((id: number) => {
    setToasts((current) => current.filter((toast) => toast.id !== id));
  }, []);

  const push = useCallback(
    (kind: ToastKind, text: string) => {
      const id = toastCounter++;
      setToasts((current) => [...current, { id, kind, text }]);
      window.setTimeout(() => dismiss(id), 7000);
    },
    [dismiss],
  );

  return { toasts, push, dismiss };
}

function taskTitleFromPayload(payload: unknown): string | null {
  if (!isRecord(payload)) return null;
  const task = payload['task'];
  if (!isRecord(task)) return null;
  const title = task['title'];
  return typeof title === 'string' ? title : null;
}

export function App(): ReactNode {
  const [session, setSession] = useState<SessionState>({ phase: 'loading' });
  const [sessionError, setSessionError] = useState<string | null>(null);
  const [tab, setTab] = useState<TabId>('board');
  const [revision, setRevision] = useState(0);
  const [cloudRevision, setCloudRevision] = useState(0);
  const [passwordOpen, setPasswordOpen] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);
  const { toasts, push, dismiss } = useToasts();

  const loadSession = useCallback(async (): Promise<void> => {
    try {
      const result = await api.get<SessionResponse>('/api/auth/session');
      if (result.user === null) {
        setSession({ phase: 'anonymous' });
        return;
      }
      setSessionError(null);
      setSession({
        phase: 'ready',
        session: {
          user: result.user,
          permissions: result.permissions ?? result.user.permissions,
          roles: result.roles ?? [],
        },
      });
    } catch (cause: unknown) {
      setSessionError(errorMessage(cause));
      setSession({ phase: 'anonymous' });
    }
  }, []);

  useEffect(() => {
    void loadSession();
  }, [loadSession]);

  const onRealtimeEvent = useCallback(
    (event: RealtimeEvent): void => {
      switch (event.type) {
        case 'task.created':
        case 'task.updated':
        case 'task.deleted':
        case 'roles.changed':
        case 'players.changed':
        case 'users.changed':
          setRevision((value) => value + 1);
          break;
        case 'task.assignedToYou': {
          const title = taskTitleFromPayload(event.payload);
          push('info', title === null ? 'Dir wurde eine Aufgabe zugewiesen.' : `Dir zugewiesen: ${title}`);
          setRevision((value) => value + 1);
          break;
        }
        case 'cloud.status':
          setCloudRevision((value) => value + 1);
          break;
        default:
          break;
      }
    },
    [push],
  );

  const realtime = useRealtime(session.phase === 'ready', onRealtimeEvent);

  const availableTabs = useMemo(() => {
    if (session.phase !== 'ready') return [] as { id: TabId; label: string }[];
    const permissions = session.session.permissions;
    const tabs: { id: TabId; label: string }[] = [];
    if (permissions.includes(PERMISSIONS.TASK_VIEW)) tabs.push({ id: 'board', label: 'Board' });
    if (permissions.includes(PERMISSIONS.HISTORY_VIEW)) tabs.push({ id: 'history', label: 'Historie' });
    if (permissions.includes(PERMISSIONS.USER_MANAGE)) tabs.push({ id: 'team', label: 'Team' });
    if (permissions.includes(PERMISSIONS.TASK_VIEW)) tabs.push({ id: 'cloud', label: 'Cloud' });
    return tabs;
  }, [session]);

  const activeTab: TabId = availableTabs.some((entry) => entry.id === tab)
    ? tab
    : availableTabs[0]?.id ?? 'board';

  const handleLoggedIn = useCallback(
    (result: LoginResponse): void => {
      setSessionError(null);
      setSession({
        phase: 'ready',
        session: {
          user: result.user,
          permissions: result.permissions.length > 0 ? result.permissions : result.user.permissions,
          roles: [],
        },
      });
      setTab('board');
      push('success', `Willkommen, ${result.user.displayName}.`);
      void loadSession();
    },
    [loadSession, push],
  );

  const handleLogout = useCallback((): void => {
    void (async () => {
      setLoggingOut(true);
      try {
        await api.post<OkResponse>('/api/auth/logout', {});
      } catch (cause: unknown) {
        push('error', errorMessage(cause));
      } finally {
        setLoggingOut(false);
        setSession({ phase: 'anonymous' });
        setTab('board');
      }
    })();
  }, [push]);

  if (session.phase === 'loading') {
    return (
      <div className="app-loading">
        <Loading label="Sitzung wird geprüft …" />
      </div>
    );
  }

  if (session.phase === 'anonymous') {
    return (
      <div className="app-root">
        {sessionError !== null && (
          <div className="login-error-wrap">
            <ErrorBox message={sessionError} onRetry={() => void loadSession()} />
          </div>
        )}
        <LoginPage onLoggedIn={handleLoggedIn} />
        <ToastStack toasts={toasts} onDismiss={dismiss} />
      </div>
    );
  }

  const { user, permissions } = session.session;

  // Ein Rang ohne task.view (z. B. "spieler") hat keinen Zugriff auf irgendeinen
  // Tab. Ohne diesen Zweig sähe die Person nur den Kopfbereich und sonst nichts.
  if (availableTabs.length === 0) {
    return (
      <div className="app-root">
        <header className="app-header">
          <div className="header-brand">
            <span className="brand-mark" aria-hidden="true">
              ⛏
            </span>
            <span className="brand-text">Team-Taskboard</span>
          </div>
          <div className="header-right">
            <UserMenu
              user={user}
              connected={realtime.connected}
              onOpenPassword={() => setPasswordOpen(true)}
              onLogout={handleLogout}
              loggingOut={loggingOut}
            />
          </div>
        </header>
        <main className="app-main">
          <HintBox
            text={`Dein Rang "${user.role?.displayName ?? user.roleId ?? 'unbekannt'}" hat keine Berechtigung für das Taskboard. Ein Teammitglied kann deinen Rang in der Cloud anpassen.`}
          />
        </main>
        {passwordOpen && (
          <PasswordDialog
            onClose={() => setPasswordOpen(false)}
            onSuccess={(message) => {
              push('success', message);
              void loadSession();
            }}
          />
        )}
        <ToastStack toasts={toasts} onDismiss={dismiss} />
      </div>
    );
  }

  return (
    <div className="app-root">
      <header className="app-header">
        <div className="header-brand">
          <span className="brand-mark" aria-hidden="true">
            ⛏
          </span>
          <span className="brand-text">Team-Taskboard</span>
        </div>

        <nav className="tabs" aria-label="Hauptnavigation">
          {availableTabs.map((entry) => (
            <button
              key={entry.id}
              type="button"
              className={entry.id === activeTab ? 'tab tab-active' : 'tab'}
              aria-current={entry.id === activeTab ? 'page' : undefined}
              onClick={() => setTab(entry.id)}
            >
              {entry.label}
            </button>
          ))}
        </nav>

        <div className="header-right">
          <UserMenu
            user={user}
            connected={realtime.connected}
            onOpenPassword={() => setPasswordOpen(true)}
            onLogout={handleLogout}
            loggingOut={loggingOut}
          />
        </div>
      </header>

      <main className="app-main">
        {!realtime.connected && realtime.lastError !== null && (
          <HintBox text={`Echtzeit-Verbindung: ${realtime.lastError}`} />
        )}

        {activeTab === 'board' && (
          <BoardPage
            permissions={permissions}
            revision={revision}
            onToast={push}
            onPermissionLost={(message) => push('error', message)}
          />
        )}
        {activeTab === 'history' && <HistoryPage revision={revision} />}
        {activeTab === 'team' && (
          <TeamPage
            permissions={permissions}
            revision={revision}
            currentUserId={user.id}
            onToast={push}
          />
        )}
        {activeTab === 'cloud' && (
          <CloudPage
            permissions={permissions}
            revision={revision}
            liveRevision={cloudRevision}
            onToast={push}
          />
        )}
      </main>

      {passwordOpen && (
        <PasswordDialog
          onClose={() => setPasswordOpen(false)}
          onSuccess={(message) => {
            push('success', message);
            void loadSession();
          }}
        />
      )}

      <ToastStack toasts={toasts} onDismiss={dismiss} />
    </div>
  );
}

function ToastStack({
  toasts,
  onDismiss,
}: {
  toasts: Toast[];
  onDismiss: (id: number) => void;
}): ReactNode {
  if (toasts.length === 0) return null;
  return (
    <div className="toast-stack" aria-live="polite">
      {toasts.map((toast) => (
        <button
          key={toast.id}
          type="button"
          className={`toast toast-${toast.kind}`}
          onClick={() => onDismiss(toast.id)}
          title="Meldung schließen"
        >
          {toast.text}
        </button>
      ))}
    </div>
  );
}
